import { useEffect, useState } from "react";
import { app } from "@microsoft/teams-js";

let teamsHostPromise: Promise<boolean> | null = null;

/** Whether the app is hosted in Teams; undefined until Teams initialization has settled. */
export const useIsInTeams = (): boolean | undefined => {
    const [inTeams, setInTeams] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        initializeTeams().then((value) => {
            if (!cancelled) {
                setInTeams(value);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return inTeams;
};

const TOP_LEVEL_INITIALIZE_TIMEOUT_MS = 5000;

// In a top-level browser window opened by another page (window.opener), the Teams SDK sends its handshake to the
// opener and waits forever. Teams desktop/web host the app in an iframe and Teams mobile provides a native
// interface, so a timeout is only applied when neither is present.
const needsInitializeTimeout = (): boolean => {
    try {
        const isTopLevel = window.self === window.top;
        return isTopLevel && !(window as unknown as { nativeInterface?: unknown }).nativeInterface;
    } catch {
        return false;
    }
};

/**
 * Initializes the Teams SDK once and resolves whether the app is hosted in Teams.
 * Outside Teams (plain browser) initialization fails, so callers can skip Teams-only APIs such as app.getContext().
 */
export const initializeTeams = (): Promise<boolean> => {
    if (!teamsHostPromise) {
        const initialize = app.initialize().then(
            () => true,
            (error) => {
                console.info("Not running in Teams.", error);
                return false;
            }
        );

        teamsHostPromise = needsInitializeTimeout() ? withTimeout(initialize) : initialize;
    }

    return teamsHostPromise;
};

const withTimeout = (initialize: Promise<boolean>): Promise<boolean> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<boolean>((resolve) => {
        timer = setTimeout(() => {
            console.info("Teams initialization timed out, assuming the app is not running in Teams.");
            resolve(false);
        }, TOP_LEVEL_INITIALIZE_TIMEOUT_MS);
    });

    return Promise.race([initialize, timeout]).finally(() => clearTimeout(timer));
};

/** Returns the Teams deep link sub page id, or undefined outside Teams. */
export const getTeamsSubPageId = async (): Promise<string | undefined> => {
    if (!(await initializeTeams())) {
        return undefined;
    }

    const context = await app.getContext();
    return context.page?.subPageId || undefined;
};
